import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';
import { getSiteSettings } from '../../services/settings';

// Turns a YouTube watch/short/embed URL into an embeddable id, else null.
const youTubeId = (url: string): string | null => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const VideoPlayer: React.FC<{ url?: string }> = ({ url }) => {
  if (!url) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-xl bg-gray-100 text-center text-gray-400">
        <div>
          <i className="ri-film-line text-4xl"></i>
          <p className="mt-2 text-sm">Video coming soon</p>
        </div>
      </div>
    );
  }
  const yt = youTubeId(url);
  if (yt) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
        <iframe
          src={`https://www.youtube.com/embed/${yt}`}
          title="Tutorial video"
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <video src={url} controls playsInline className="aspect-video w-full rounded-xl bg-black" />
  );
};

const TutorialsPage = () => {
  const [sellerVideo, setSellerVideo] = useState<string | undefined>();
  const [buyerVideo, setBuyerVideo] = useState<string | undefined>();
  const [items, setItems] = useState<{ id: string; title: string; description?: string; videoUrl?: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const s = await getSiteSettings();
        setSellerVideo(s.tutorials?.sellerVideoUrl);
        setBuyerVideo(s.tutorials?.buyerVideoUrl);
        setItems(Array.isArray(s.tutorials?.items) ? s.tutorials!.items! : []);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <CampusHeader />
      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Hero */}
        <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900 px-6 py-10 md:px-10 md:py-12 text-center">
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-teal-500/20 blur-2xl"></div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-teal-200 ring-1 ring-white/15">
            <i className="ri-play-circle-line"></i> How it works
          </span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-white">Getting Started with Campus Market</h1>
          <p className="mx-auto mt-2 max-w-xl text-sm md:text-base text-gray-300">
            Watch these short walkthroughs to register and start buying or selling in minutes.
          </p>
        </div>

        {loading ? (
          <div className="space-y-8">
            {[0, 1].map(i => (
              <div key={i} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
                <div className="aspect-video w-full animate-pulse rounded-xl bg-gray-200" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-8">
            {/* Seller */}
            <section className="rounded-2xl bg-white p-4 md:p-6 shadow-sm ring-1 ring-gray-100">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 text-teal-600"><i className="ri-store-2-line text-xl"></i></span>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">How to register as a Seller</h2>
                  <p className="text-sm text-gray-500">Set up your seller account and start listing.</p>
                </div>
              </div>
              <VideoPlayer url={sellerVideo} />
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/sell" className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700">
                  Become a Seller <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </section>

            {/* Buyer */}
            <section className="rounded-2xl bg-white p-4 md:p-6 shadow-sm ring-1 ring-gray-100">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600"><i className="ri-shopping-bag-3-line text-xl"></i></span>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">How to register as a Buyer</h2>
                  <p className="text-sm text-gray-500">Create an account and shop safely with escrow.</p>
                </div>
              </div>
              <VideoPlayer url={buyerVideo} />
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/auth/sign-up" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                  Create an Account <i className="ri-arrow-right-line"></i>
                </Link>
                <Link to="/marketplace" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                  Browse Marketplace
                </Link>
              </div>
            </section>

            {/* Additional tutorials (admin-managed) */}
            {items.filter((it) => it.videoUrl || it.title).map((it) => (
              <section key={it.id} className="rounded-2xl bg-white p-4 md:p-6 shadow-sm ring-1 ring-gray-100">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100 text-purple-600"><i className="ri-play-circle-line text-xl"></i></span>
                  <div>
                    <h2 className="text-lg md:text-xl font-bold text-gray-900">{it.title || 'Tutorial'}</h2>
                    {it.description && <p className="text-sm text-gray-500">{it.description}</p>}
                  </div>
                </div>
                <VideoPlayer url={it.videoUrl} />
              </section>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default TutorialsPage;
